import { Product, ProductPriceOffer } from '@@types'
import { TFunction } from 'i18next'

import { formatGramsToHumanWeight } from '@shared/uttils/formatGramsToHumanWeight'
import { getWeightPostfix } from '@shared/uttils/weightHelpers'


export type MaxAmountResult = {
  maxAmount: number;
  source: 'max_amount' | 'stock' | 'default';
};

export type ValidationResult = {
  isValid: boolean;
  normalized: number;
  min: number;
  max: number;
  step: number;
};

export const getMinAmount = (product: Product): number => {
  if (!product?.priceOffer) return 1
  const minFromOffer = product.priceOffer.min
  return minFromOffer > 0 ? minFromOffer : (product.priceOffer.step)
}

export const normalizeAmount = (amount: number, step: number): number => {
  if (step <= 0) return amount
  const multiplier = 1 / step
  return Math.round(amount * multiplier) / multiplier
}

export const validateAmount = (
  amount: number,
  product: Product,
): ValidationResult => {
  const step = getAmountStep(product)
  const min = getMinAmount(product)
  const { maxAmount } = getMaxAmount(product)

  const normalized = normalizeAmount(amount, step)
  const finalAmount = Math.max(min, Math.min(normalized, maxAmount))

  return {
    isValid: normalized >= min && normalized <= maxAmount,
    normalized: finalAmount,
    min,
    max: maxAmount,
    step,
  }
}

export const getCountPostfix = (product: Product, t: TFunction): string => {
  const uiQuant = product.priceOffer?.uiQuant
  if (uiQuant) {
    switch (uiQuant) {
    case 'KGM':
      return t('kg')
    case 'GRM':
      return t('gr')
    case 'XPC':
      return t('pc')
    default:
      return t('pc')
    }
  }
  
  // Fallback to old logic if uiQuant is not available
  switch (product.price_type) {
  case 'piece':
    return t('pc')
  case 'weight':
    return getWeightPostfix(product, t)
  default:
    return ''
  }
}

export const getAmountInQuantType = (priceOffer: ProductPriceOffer): number => {
  switch (priceOffer.quant) {
  case 'KGM':
    return 1000
  default:
    return 1
  }
}

export const getAmountInBaseQuant = (priceOffer: ProductPriceOffer): number =>
  (priceOffer.quantAmount ?? 1) * getAmountInQuantType(priceOffer)


type FormatAmountWithQuantArgs = {
  t: TFunction;
  quantType: ProductPriceOffer;
  amount: number;
}

export const formatAmountWithQuant = ({
  t,
  quantType,
  amount
}: FormatAmountWithQuantArgs): string => {

  return `${amount} ${t(`product.${quantType}`)}`
}

type FormatAmountWithUnitMultiplierArgs = {
  product: Product;
  unit_multiplier: number;
  t: TFunction;
}

export const formatAmountWithUnitMultiplier = ({
  product,
  unit_multiplier,
  t
}: FormatAmountWithUnitMultiplierArgs): string => {
  const isWeight = isWeightProduct(product.price_type)

  if (!isWeight) {
    return `${unit_multiplier} ${t('weight.pc')}`
  }

  return formatGramsToHumanWeight({
    weight: unit_multiplier,
    t,
    showDecimal: true,
  })
}

type FormatPriceAmountArgs = {
  priceOffer: ProductPriceOffer;
  t: TFunction;
}

export const formatPriceAmount = ({
  priceOffer,
  t
}: FormatPriceAmountArgs): string => {
  const quantType = priceOffer.quant || 'XPC'
  const quantAmount = priceOffer.quantAmount || 1

  return `${quantAmount} ${t(`product.${quantType}`)}`
}

type FormatAmountArgs = {
  product: Product;
  amount: number;
  t: TFunction;
  showOne?: boolean;
};

export const formatAmount = ({
  product,
  amount,
  t,
  showOne = false,
}: FormatAmountArgs): string => {
  if (isWeightProduct(product.price_type)) {
    return formatGramsToHumanWeight({
      weight: amount,
      t,
    })
  }
  return `${amount > 1 || showOne ? `${amount} ` : ''}${t('weight.pc')}`
}

export const isWeightProduct = (price_type: 'weight' | 'piece'): boolean =>
  price_type === 'weight'

export const stockAmount = (
  product: Product,
  defaultValue: number = 0,
): number => {
  const amount = product?.stock?.amount ?? 0
  return amount > 0 ? amount : defaultValue
}

export const getMaxAmount = (product: Product): MaxAmountResult => {
  const priceOffer = product.priceOffer
  const availableStock = stockAmount(product, 0)

  if (
    (priceOffer?.max) > 0 &&
    (availableStock === 0 || (priceOffer.max) < availableStock)
  ) {
    return {
      maxAmount: priceOffer.max,
      source: 'max_amount',
    }
  }

  if (availableStock > 0) {
    return {
      maxAmount: availableStock,
      source: 'stock',
    }
  }

  return {
    maxAmount: 99999,
    source: 'default',
  }
}

export const getAmountStep = (product: Product): number => {
  const priceOffer = product.priceOffer
  return (priceOffer.step) > 0 ? (priceOffer.step) : 1
}

export const isCartButtonDisabled = (product: Product): boolean => {
  return stockAmount(product, 0) <= 0
}

export const calculateItemTotal = (
  price: number,
  amount?: number,
  countMultiplier: number = 1,
): number => {
  return (price * (amount ?? 0)) / countMultiplier
}

export const isAmountValid = (amount: number, product: Product): boolean => {
  const min = getMinAmount(product)
  const { maxAmount } = getMaxAmount(product)
  const step = getAmountStep(product)
  const normalized = normalizeAmount(amount, step)

  return normalized >= min && normalized <= maxAmount
}

export const getNextValidAmount = (
  currentAmount: number,
  product: Product,
  direction: 1 | -1 = 1,
): number => {
  const step = getAmountStep(product)
  const newAmount = normalizeAmount(currentAmount + step * direction, step)
  const { min, max } = validateAmount(newAmount, product)

  return Math.min(Math.max(newAmount, min), max)
}
